/**
 * UCC Submissions Hooks
 *
 * React Query hooks for electronic filing submission operations.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { uccFilingSubmissionClient } from '../api/typed-clients';
import { retryUnlessClientError, unwrapUccResponse } from './ucc-response';

const SUBMISSIONS_KEY = 'uccSubmissions';

export function useSubmissions(params: { tenantId: string; status?: string; jurisdictionCode?: string; page?: number; limit?: number }) {
  return useQuery({
    queryKey: [SUBMISSIONS_KEY, params],
    queryFn: async () => {
      const result = await uccFilingSubmissionClient.GET('/filing-submissions', {
        params: {
          query: {
            tenantId: params.tenantId,
            status: params.status as never,
            jurisdictionCode: params.jurisdictionCode,
            page: params.page,
            limit: params.limit,
          },
        },
      });
      return unwrapUccResponse(result);
    },
    enabled: !!params.tenantId,
    // A 404 means the service is not deployed; say so on the first answer.
    retry: retryUnlessClientError,
  });
}

export function useSubmission(submissionId: string | undefined) {
  return useQuery({
    queryKey: [SUBMISSIONS_KEY, submissionId],
    queryFn: async () => {
      const result = await uccFilingSubmissionClient.GET('/filing-submissions/{submissionId}', {
        params: { path: { submissionId: submissionId! } },
      });
      return unwrapUccResponse(result);
    },
    enabled: !!submissionId,
  });
}

export function useInitiateSubmission() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: never) => {
      const result = await uccFilingSubmissionClient.POST('/filing-submissions', { body });
      return unwrapUccResponse(result);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SUBMISSIONS_KEY] });
    },
  });
}

export function useRetrySubmission() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (submissionId: string) => {
      const result = await uccFilingSubmissionClient.POST('/filing-submissions/{submissionId}/retry', {
        params: { path: { submissionId } },
      });
      return unwrapUccResponse(result);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SUBMISSIONS_KEY] });
    },
  });
}
